import React, { useState, useEffect } from "react";
import PageWrapper from "../components/PageWrapper";
import { BaseURL, getData } from "../FatchApi/FatchApi";
import Spinner from "../components/Loader/Spinner";
import axios from "axios";

const ManagePopup = () => {
  const [popup, setPopup] = useState(null);
  const [newPopup, setNewPopup] = useState({ ctaUrl: '', title: '', imageDataUrl: '' });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPopup();
  }, []);

  const fetchPopup = async () => {
    try {
      const res = await getData('api/popup/popup');
      if (res.popup) setPopup(res.popup);
    } catch (error) {
      console.error('No popup found');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size < 1024 * 1024) { // 1MB
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPopup({...newPopup, imageDataUrl: reader.result});
      };
      reader.readAsDataURL(file);
    } else {
      alert('Image must be <1MB. Recommended: 500x500px square, AVIF/WebP');
    }
  };

  const toggleActive = async () => {
    setLoading(true);
    try {
      const res = await axios.put(`${BaseURL}/api/popup/popup-update/${popup._id}`, { active: !popup.active });
      if (res.data.success) {
        setPopup({...popup, active: !popup.active});
      }
    } catch (error) {
      alert('Toggle failed');
    }
    setLoading(false);
  };

  const updatePopup = async () => {
    setUploading(true);
    try {
      const res = await axios.post(`${BaseURL}/api/popup/popup-upload`, newPopup);
      if (res.data.success) {
        fetchPopup();
        setNewPopup({ ctaUrl: '', title: '', imageDataUrl: '' });
        alert('Popup created successfully!');
      }
    } catch (error) {
      alert(`Upload failed: ${error?.response?.data?.error || error.message}`);
    }
    setUploading(false);
  };

  const deletePopup = async () => {
    if (window.confirm('Delete popup?')) {
      setUploading(true);
      try {
        await axios.delete(`${BaseURL}/api/popup/popup/${popup._id}`);
        setPopup(null);
        setNewPopup({ ctaUrl: '', title: '', imageDataUrl: '' });
      } catch (error) {
        alert(`Delete failed: ${error?.response?.data?.error || error.message}`);
      }
      setUploading(false);
    }
  };

  return (
    <PageWrapper>
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8 text-center">
          Popup Management
        </h1>
        
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-3xl p-8 mb-8 shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-6">
            Current Popup Status
          </h2>
          
          {popup ? (
            <div className="space-y-4">
              <div className="relative w-64 h-64 mx-auto rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={popup.image.url} 
                  alt="Popup"
                  className="w-full h-full object-cover"
                />
                {popup.ctaUrl && (
                  <div className="absolute inset-0 bg-blue-500/30 flex items-center justify-center hover:bg-blue-500/50 transition-all">
                    <a href={popup.ctaUrl} target="_blank" className="bg-white px-6 py-3 rounded-lg font-bold text-blue-600 hover:text-blue-700">
                      {popup.title || 'Visit Now'}
                    </a>
                  </div>
                )}
              </div>
              <div className="text-center space-y-2">
                <div className="text-xl font-bold text-white">{popup.title || 'Untitled Popup'}</div>
                <div className="text-blue-100">
                  CTA: <a href={popup.ctaUrl} target="_blank" className="underline hover:text-white">{popup.ctaUrl}</a>
                </div>
                <button
                  onClick={toggleActive}
                  disabled={loading}
                  className="px-8 py-3 bg-white text-purple-600 font-bold rounded-xl hover:bg-gray-100 transition-all disabled:opacity-50"
                >
                  {popup.active ? '❌ Deactivate' : '✅ Activate'}
                </button>
                <button
                  onClick={deletePopup}
                  disabled={uploading}
                  className="px-8 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all ml-4"
                >
                  Delete Popup
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-white mb-2">No Popup Active</h3>
              <p className="text-blue-100 mb-8">Create your first popup below (shows 3s after page load)</p>
            </div>
          )}
        </div>

        {/* Create/Edit Form */}
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Create New Popup</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <label className="block mb-2 font-bold text-white text-lg">
                Popup Image
                <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  500x500px square, Less then 1MB AVIF/WebP
                </span>
              </label>
              <input
                type="file"
                id="popupImage"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full p-3 border-2 border-dashed border-white/30 rounded-2xl bg-white/10 text-white file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:bg-gradient-to-r file:from-blue-500 file:to-purple-500 file:text-white hover:file:from-blue-600 file:text-lg font-bold"
              />
              {newPopup.imageDataUrl && (
                <div className="mt-4">
                  <img 
                    src={newPopup.imageDataUrl} 
                    className="w-32 h-32 object-cover rounded-xl shadow-lg mx-auto"
                    alt="Preview"
                  />
                </div>
              )}
            </div>
            
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-bold text-white">CTA URL *</label>
                <input
                  type="url"
                  value={newPopup.ctaUrl}
                  onChange={(e) => setNewPopup({...newPopup, ctaUrl: e.target.value})}
                  placeholder="https://example.com/promo"
                  className="w-full p-3 border border-white/30 rounded-xl bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/50"
                  required
                />
              </div>
              <div>
                <label className="block mb-2 font-bold text-white">Title (optional)</label>
                <input
                  type="text"
                  value={newPopup.title}
                  onChange={(e) => setNewPopup({...newPopup, title: e.target.value})}
                  placeholder="Summer Sale 50% OFF"
                  className="w-full p-3 border border-white/30 rounded-xl bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/50 max-h-16"
                  maxLength={50}
                />
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={updatePopup}
              disabled={!newPopup.ctaUrl || !newPopup.imageDataUrl}
              className="px-12 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold rounded-2xl text-lg hover:from-green-600 hover:to-blue-600 hover:shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
            >
              {uploading ? 'Uploading...' : '💫 Create/Update Popup'}
            </button>
            {popup && (
              <button
                onClick={deletePopup}
                className="px-12 py-4 bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold rounded-2xl text-lg hover:from-red-600 hover:to-pink-600 hover:shadow-2xl hover:scale-105 transition-all shadow-xl"
              >
                🗑️ Delete
              </button>
            )}
          </div>
        </div>
      </div>
      
      {loading && <Spinner />}
    </PageWrapper>
  );
};

export default ManagePopup;
